import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import MenuBar from '../../components/MenuBar'
import httpClient from '../../components/helpers/httpClient'
import PopupDropdown from '../../components/PopupDropdown'
import { DateField } from '@mui/x-date-pickers'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { toast } from 'react-toastify'
import { parseDateValue, serializeDate } from '../../components/helpers/formatDate'
import { NumericFormat } from 'react-number-format'
import './TransactionDetailPage.css'
import Button from '../../components/UI/Button'
import { formatPropertyIdentifier } from '../../components/helpers/propertyIdentifier'
import TransactionUnitTargets from '../../components/transactions/TransactionUnitTargets'
import OwnershipComposer from '../../components/transactions/OwnershipComposer'
import {
  createEmptyOwnershipPlan,
  hasOwnershipHolders,
  legacyParticipantsToOwnershipPlan,
  normalizeOwnershipPlan,
  ownershipPlanToLegacyParticipants,
  validateOwnershipPlan,
} from '../../components/transactions/ownershipPlan'

function CreateTransactionPage() {
  const navigate = useNavigate()

  const [loadingProperties, setLoadingProperties] = useState(false)
  const [properties, setProperties] = useState([])
  const [newTransactionObj, setNewTransactionObj] = useState({
    type: "",
    reservationDate: null,
    contractDate: null,
    deedDate: null,
    landPrice: "",
    constructionPrice: "",
    transactionPrice: "0",
    sellers: [],
    buyers: [],
    properties: [],
    units: [],
    notaryBuyers: [],
    notarySellers: [],
    brokers: [],
    isAssetDeal: true,
    isShareDeal: false,
    transactedCompanyId: null,
    estimatedAssetValue: ""
  })
  const [selectedProperties, setSelectedProperties] = useState([])
  const [selectedUnits, setSelectedUnits] = useState([])
  const [contacts, setContacts] = useState([])
  const [companies, setCompanies] = useState([])
  const [selectedSellers, setSelectedSellers] = useState([])
  const [selectedBuyers, setSelectedBuyers] = useState([])
  const [sellerOwnershipPlan, setSellerOwnershipPlan] = useState(createEmptyOwnershipPlan)
  const [buyerOwnershipPlan, setBuyerOwnershipPlan] = useState(createEmptyOwnershipPlan)
  const [selectedNotaryBuyers, setSelectedNotaryBuyers] = useState([])
  const [selectedNotarySellers, setSelectedNotarySellers] = useState([])
  const [selectedBrokers, setSelectedBrokers] = useState([])
  const [selectedTransactedCompany, setSelectedTransactedCompany] = useState(null)
  const [menuItem, setMenuItem] = useState("properties")

  const BASISAKTE_TRANSACTION_TYPES = ["Basisakte", "Wijzigende basisakte"]

  // Check if current type should be treated as Basisakte to disable certain fields
  const isBasisakte = BASISAKTE_TRANSACTION_TYPES.includes(newTransactionObj.type)

  const fetchLatestBuyerForProperties = async (propertyIds) => {
    if (!propertyIds || propertyIds.length === 0) return null
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/transactions/latest-buyer?propertyIds=${propertyIds.join(',')}&structured=true`
      )
      if (response.status === 200 && response.data) {
        return response.data
      }
    } catch (error) {
      console.log("Error fetching latest buyer:", error)
    }
    return null
  }

  const fetchProperties = async () => {
    setLoadingProperties(true)
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/properties/get?type=including-projects-units`)
    if (response.status == 200) {
      setProperties(response.data)
    }
    setLoadingProperties(false)
  }

  const fetchContacts = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/contacts/get`)
    if (response.status == 200) {
      setContacts(response.data)
    }
  }

  const fetchCompanies = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get?type=limited-info`)
    if (response.status == 200) {
      setCompanies(response.data)
    }
  }

  useEffect(() => {
    fetchProperties()
    fetchContacts()
    fetchCompanies()
  }, [])

  useEffect(() => {
    setNewTransactionObj(prev => ({
      ...prev,
      sellers: selectedSellers,
      buyers: selectedBuyers,
      properties: selectedProperties,
      units: selectedUnits,
      notaryBuyers: selectedNotaryBuyers,
      notarySellers: selectedNotarySellers,
      brokers: selectedBrokers,
      transactedCompanyId: selectedTransactedCompany?.id || null
    }))
  }, [selectedBuyers, selectedProperties, selectedUnits, selectedSellers, selectedNotaryBuyers, selectedNotarySellers, selectedBrokers, selectedTransactedCompany])

  const handleSellerOwnershipPlanChange = nextPlan => {
    setSellerOwnershipPlan(nextPlan)
    setSelectedSellers(ownershipPlanToLegacyParticipants(nextPlan))
  }

  const handleBuyerOwnershipPlanChange = nextPlan => {
    setBuyerOwnershipPlan(nextPlan)
    setSelectedBuyers(ownershipPlanToLegacyParticipants(nextPlan))
  }

  const handleSetStateNewTransactionType = async (value) => {
    setNewTransactionObj({ ...newTransactionObj, type: value })
    
    // If Basisakte is selected, auto-populate buyer and seller with the current owners
    if (BASISAKTE_TRANSACTION_TYPES.includes(value) && selectedProperties.length > 0) {
      const propertyIds = selectedProperties.map(p => p.id)
      const latestOwnership = await fetchLatestBuyerForProperties(propertyIds)
      const latestBuyers = Array.isArray(latestOwnership) ? latestOwnership : latestOwnership?.buyers
      
      if (latestBuyers && latestBuyers.length > 0) {
        const ownerEntries = latestBuyers.map(buyer => ({
          id: buyer.id,
          first_name: buyer.first_name || '',
          last_name: buyer.last_name || '',
          company_name: buyer.company_name || '',
          type: buyer.type,
          ownership_percentage: buyer.ownership_percentage || "100",
          ownership_type: buyer.ownership_type || "volle eigendom",
          ownership_right_end_date: buyer.ownership_right_end_date || null
        }))
        const latestPlan = normalizeOwnershipPlan(
          (!Array.isArray(latestOwnership) && hasOwnershipHolders(latestOwnership.buyerOwnershipPlan))
            ? latestOwnership.buyerOwnershipPlan
            : legacyParticipantsToOwnershipPlan(ownerEntries),
        )
        handleSellerOwnershipPlanChange(normalizeOwnershipPlan(latestPlan))
        handleBuyerOwnershipPlanChange(normalizeOwnershipPlan(latestPlan))
      }
    }
  }

  const handleSetStateNewTransactionObj = (field, value) => {
    setNewTransactionObj({ ...newTransactionObj, [field]: value })
  }

  const handleSave = async () => {
    const requiredFields = {
      type: "Transaction type",
      sellers: "Sellers",
      buyers: "Buyers",
    }

    if (selectedProperties.length === 0 && selectedUnits.length === 0) {
      toast.error('At least one property or unit is required')
      return
    }

    const planChecks = [
      ['Seller ownership', validateOwnershipPlan(sellerOwnershipPlan)],
      ['Buyer ownership', validateOwnershipPlan(buyerOwnershipPlan)],
    ]
    const invalidPlan = planChecks.find(([, result]) => !result.valid)
    if (invalidPlan) {
      toast.error(`${invalidPlan[0]}: ${invalidPlan[1].errors[0]}`)
      return
    }

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!newTransactionObj[field] || (Array.isArray(newTransactionObj[field]) && newTransactionObj[field].length === 0)) {
        toast.error(`${label} is required`)
        return
      }
    }

    const propertiesWithPrices = newTransactionObj.properties.map(property => ({
      id: property.id,
      land_price: property.land_price || 0,
      construction_price: property.construction_price || 0,
      connection_cost: property.connection_cost || 0,
      contract_cost: property.contract_cost || 0,
      vat_percentage: property.vat_percentage || 0.21,
      registration_percentage: property.registration_percentage || 0.12
    }))

    const unitsWithPrices = selectedUnits.map(unit => ({
      id: unit.id,
      land_price: unit.land_price || 0,
      land_tax_mode: unit.land_tax_mode || 'registration',
      land_vat_percentage: unit.land_vat_percentage ?? unit.vat_percentage ?? 0.21,
      land_registration_percentage: unit.land_registration_percentage ?? unit.registration_percentage ?? 0.12,
      construction_price: unit.construction_price || 0,
      construction_tax_mode: unit.construction_tax_mode || 'vat',
      connection_cost: unit.connection_cost || 0,
      contract_cost: unit.contract_cost || 0,
      vat_percentage: unit.vat_percentage || 0.21,
      construction_vat_percentage: unit.construction_vat_percentage ?? unit.vat_percentage ?? 0.21,
      connection_vat_percentage: unit.connection_vat_percentage ?? unit.vat_percentage ?? 0.21,
      contract_vat_percentage: unit.contract_vat_percentage ?? unit.vat_percentage ?? 0.21,
      construction_registration_percentage: unit.construction_registration_percentage ?? unit.registration_percentage ?? 0.12,
      registration_percentage: unit.registration_percentage || 0.12,
    }))

    const transactionData = {
      ...newTransactionObj,
      properties: propertiesWithPrices,
      units: unitsWithPrices,
      reservationDate: serializeDate(newTransactionObj.reservationDate),
      contractDate: serializeDate(newTransactionObj.contractDate),
      deedDate: serializeDate(newTransactionObj.deedDate),
      notaryBuyers: newTransactionObj.notaryBuyers || [],
      notarySellers: newTransactionObj.notarySellers || [],
      brokers: newTransactionObj.brokers || [],
      isAssetDeal: Boolean(newTransactionObj.isAssetDeal),
      isShareDeal: Boolean(newTransactionObj.isShareDeal),
      transactedCompanyId: newTransactionObj.isShareDeal ? (selectedTransactedCompany?.id || null) : null,
      estimatedAssetValue: newTransactionObj.isShareDeal ? (newTransactionObj.estimatedAssetValue ?? null) : null,
      sellerOwnershipPlan,
      buyerOwnershipPlan,
    }

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + `/api/transactions/create`, transactionData)
      if (response.status == 200) {
        toast.success("Transaction created successfully")
        navigate('/transactions')
      } else {
        toast.error("Error creating transaction")
      }
    } catch (e) {
      toast.error(e.response?.data?.errors?.[0] || e.response?.data?.message || "Error creating transaction")
    }
  }

  const handleSelectProperty = (property) => {
    if (selectedProperties.some(p => p.id === property.id)) return
    const commercialPrice = property.property_x_unit_set[0]?.unit_id?.commercial_price || {}
    const propertyWithPrices = {
      ...property,
      land_price: Number(commercialPrice.land_price) || 0,
      construction_price: Number(commercialPrice.construction_price) || 0,
      connection_cost: Number(commercialPrice.connection_cost) || 0,
      contract_cost: Number(commercialPrice.contract_cost) || 0,
      vat_percentage: Number(commercialPrice.vat_percentage) || 0.21,
      registration_percentage: Number(commercialPrice.registration_percentage) || 0.12
    }
    setSelectedProperties([...selectedProperties, propertyWithPrices])
  }

  const handleRemoveProperty = (property) => { setSelectedProperties(selectedProperties.filter(p => p.id !== property.id)) }
  const handleSetStateNewTransactionNotaryBuyers = (values) => { setSelectedNotaryBuyers(values) }
  const handleSetStateNewTransactionNotarySellers = (values) => { setSelectedNotarySellers(values) }
  const handleSetStateNewTransactionBrokers = (values) => { setSelectedBrokers(values) }

  const handlePropertyPriceUpdate = (propertyId, field, value) => {
    setSelectedProperties(prevProperties => prevProperties.map(property => property.id === propertyId ? { ...property, [field]: value } : property))
    const updatedProperty = selectedProperties.find(p => p.id === propertyId)
    if (updatedProperty) {
      const updatedProperties = selectedProperties.map(p => p.id === propertyId ? { ...p, [field]: value } : p)
      setNewTransactionObj(prev => ({ ...prev, properties: updatedProperties }))
    }
  }

  const calculateTotalTransactionPrice = () => {
    const directUnitIds = new Set(selectedUnits.map(unit => unit.id))
    const propertiesWithoutDirectUnit = selectedProperties.filter(property => (
      !property.property_x_unit_set?.some(link => directUnitIds.has(link.unit_id?.id))
    ))

    return [...propertiesWithoutDirectUnit, ...selectedUnits].reduce((accumulator, target) => {
      const landPrice = Number(target.land_price) || 0
      const constructionPrice = Number(target.construction_price) || 0
      const connectionCost = Number(target.connection_cost) || 0
      const contractCost = Number(target.contract_cost) || 0
      const subtotalForVat = constructionPrice + connectionCost + contractCost
      const subtotal = landPrice + subtotalForVat
      return accumulator + subtotal
    }, 0)
  }

  useEffect(() => {
    const totalPrice = calculateTotalTransactionPrice()
    const propertiesWithExplicitPrices = selectedProperties.map(property => ({
      id: property.id,
      land_price: property.land_price || 0,
      construction_price: property.construction_price || 0,
      connection_cost: property.connection_cost || 0,
      contract_cost: property.contract_cost || 0,
      vat_percentage: property.vat_percentage || 0.21,
      registration_percentage: property.registration_percentage || 0.12,
      cadastral_nr: property.cadastral_nr,
      partition_nr: property.partition_nr,
      location: property.location,
      function: property.function,
      property_x_unit_set: property.property_x_unit_set
    }))
    setNewTransactionObj(prev => ({ ...prev, transactionPrice: totalPrice, properties: propertiesWithExplicitPrices }))
  }, [selectedProperties, selectedUnits])

  const getTotalsForProperty = (property) => {
    const landPrice = Number(property.land_price) || 0
    const constructionPrice = Number(property.construction_price) || 0
    const connectionCost = Number(property.connection_cost) || 0
    const contractCost = Number(property.contract_cost) || 0
    const vatPercentage = Number(property.vat_percentage) || 0.21
    const registrationPercentage = Number(property.registration_percentage) || 0.12
    const subtotalForVat = constructionPrice + connectionCost + contractCost
    const vatAmount = subtotalForVat * vatPercentage
    const landRegistration = landPrice * registrationPercentage
    const subtotal = landPrice + subtotalForVat
    const total = subtotal + vatAmount + landRegistration
    return { subtotal, vatAmount, landRegistration, total }
  }

  const renderPropertyRow = (property, index) => {
    const unitNames = property.property_x_unit_set.map(pxu => pxu.unit_id.unit_name).join(', ')
    const projectNames = property.property_x_unit_set
      .map(pxu => pxu.unit_id.building_id.project_id.project_placeholder_name)
      .filter((value, index, self) => self.indexOf(value) === index)
      .join(', ')
    const totals = getTotalsForProperty(property)

    return (
      <div key={index}>
        <div className='selected_property_item' onClick={() => handleRemoveProperty(property)}>
          <div className='property_item_value transaction-target-identity'>
            <span className='transaction-target-badge property'>Property</span>
            <span>{formatPropertyIdentifier(property.cadastral_nr, property.partition_nr)}</span>
          </div>
          <div className='property_item_value'>{property.location}</div>
          <div className='property_item_value'>{property.function}</div>
          <div className='property_item_value'>{unitNames}</div>
          <div className='property_item_value'>{projectNames}</div>
          <div className='property_item__icon min'>-</div>
        </div>

        {!isBasisakte && (
          <div className='property-price-details'>
            <div className="price-inputs">
              <div className="input_field--combo-grid">
                <label className="input_field__label">Land</label>
                <div className="input-with-euro">
                  <span className="euro-sign">€</span>
                  <NumericFormat
                    className="input_field__input_box--text"
                    value={property.land_price}
                    thousandSeparator=" "
                    decimalSeparator="," 
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={(values) => {
                      const { floatValue } = values
                      handlePropertyPriceUpdate(property.id, 'land_price', floatValue ?? '')
                    }}
                  />
                </div>
              </div>

              <div className="input_field--combo-grid">
                <label className="input_field__label">Construction</label>
                <div className="input-with-euro">
                  <span className="euro-sign">€</span>
                  <NumericFormat
                    className="input_field__input_box--text"
                    value={property.construction_price}
                    thousandSeparator=" "
                    decimalSeparator="," 
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={(values) => {
                      const { floatValue } = values
                      handlePropertyPriceUpdate(property.id, 'construction_price', floatValue ?? '')
                    }}
                  />
                </div>
              </div>

              <div className="input_field--combo-grid">
                <label className="input_field__label">Connection</label>
                <div className="input-with-euro">
                  <span className="euro-sign">€</span>
                  <NumericFormat
                    className="input_field__input_box--text"
                    value={property.connection_cost}
                    thousandSeparator=" "
                    decimalSeparator="," 
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={(values) => {
                      const { floatValue } = values
                      handlePropertyPriceUpdate(property.id, 'connection_cost', floatValue ?? '')
                    }}
                  />
                </div>
              </div>

              <div className="input_field--combo-grid">
                <label className="input_field__label">Contract</label>
                <div className="input-with-euro">
                  <span className="euro-sign">€</span>
                  <NumericFormat
                    className="input_field__input_box--text"
                    value={property.contract_cost}
                    thousandSeparator=" "
                    decimalSeparator="," 
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={(values) => {
                      const { floatValue } = values
                      handlePropertyPriceUpdate(property.id, 'contract_cost', floatValue ?? '')
                    }}
                  />
                </div>
              </div>

              <div className="input_field--combo-grid small">
                <label className="input_field__label">VAT</label>
                <div className="input-with-euro">
                  <NumericFormat
                    className="input_field__input_box--text"
                    value={property.vat_percentage !== '' && property.vat_percentage !== undefined && property.vat_percentage !== null ? property.vat_percentage * 100 : (property.vat_percentage === '' ? '' : 21)}
                    thousandSeparator=" "
                    decimalSeparator="," 
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={(values) => {
                      const { floatValue } = values
                      handlePropertyPriceUpdate(property.id, 'vat_percentage', floatValue !== undefined ? floatValue / 100 : '')
                    }}
                  />
                  <span className="euro-sign">%</span>
                </div>
              </div>

              <div className="input_field--combo-grid small">
                <label className="input_field__label">Registration</label>
                <div className="input-with-euro">
                  <NumericFormat
                    className="input_field__input_box--text"
                    value={property.registration_percentage !== '' && property.registration_percentage !== undefined && property.registration_percentage !== null ? property.registration_percentage * 100 : (property.registration_percentage === '' ? '' : 12)}
                    thousandSeparator=" "
                    decimalSeparator="," 
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={(values) => {
                      const { floatValue } = values
                      handlePropertyPriceUpdate(property.id, 'registration_percentage', floatValue !== undefined ? floatValue / 100 : '')
                    }}
                  />
                  <span className="euro-sign">%</span>
                </div>
              </div>
            </div>

            <div className="total-row">
              <div>Subtotal: €<NumericFormat value={totals.subtotal} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
              <div>VAT: €<NumericFormat value={totals.vatAmount} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
              <div>Land Registration: €<NumericFormat value={totals.landRegistration} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
              <div>Total (excl. VAT & Registration): €<NumericFormat value={totals.subtotal} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='template'>
      <Header>
        <div className='header__left'>
          <div className="back_button" onClick={() => navigate(-1)}><KeyboardBackspaceIcon /> Go Back</div>
        </div>
        <div className='header__options--right'>
          <Button variant='primary' size='sm' onClick={handleSave}>Save</Button>
        </div>
      </Header>
      <MenuBar />
      <div className='template__body' id='transaction-detail'>
        <div className='menu__titles'>
          <h3 className={`${menuItem == "properties" ? "selected" : ""}`} onClick={() => setMenuItem("properties")}>units & properties</h3>
          <h3 className={`${menuItem == "details" ? "selected" : ""}`} onClick={() => setMenuItem("details")}>details</h3>
        </div>

        <div className={`body__section ${menuItem}`}>
          {menuItem === 'properties' && (
            <div className='panel'>
              <div className='selected_properties_list'>
                <TransactionUnitTargets
                  selectedUnits={selectedUnits}
                  onChange={setSelectedUnits}
                  isBasisakte={isBasisakte}
                  properties={properties}
                  selectedProperties={selectedProperties}
                  loadingProperties={loadingProperties}
                  onSelectProperty={handleSelectProperty}
                />
                <div className='properties_list'>
                  {selectedProperties.map(renderPropertyRow)}
                </div>
              </div>
            </div>
          )}

          {menuItem === 'details' && (
            <>
              <div className='panel overflow-y-auto'>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="transaction_type">Type</label>
                  <PopupDropdown
                    options={["Oorsprong", "Aankoop", "Verkoop", "Basisakte", "Wijzigende basisakte"]}
                    value={newTransactionObj.type}
                    onChange={handleSetStateNewTransactionType}
                    title={"Select type"}
                    searchBar={false}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="reservation_date">Reservation date</label>
                  <DateField
                    className="date-picker"
                    format="DD-MM-YYYY"
                    value={parseDateValue(newTransactionObj.reservationDate)}
                    onChange={(value) => {
                      const formattedDate = serializeDate(value)
                      handleSetStateNewTransactionObj("reservationDate", formattedDate)
                    }}
                    disabled={isBasisakte}
                  />
                </div>
                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="contract_date">Contract date</label>
                  <DateField
                    className="date-picker"
                    format="DD-MM-YYYY"
                    value={parseDateValue(newTransactionObj.contractDate)}
                    onChange={(value) => {
                      const formattedDate = serializeDate(value)
                      handleSetStateNewTransactionObj("contractDate", formattedDate)
                    }}
                    disabled={isBasisakte}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="deed_date">Deed date</label>
                  <DateField
                    className="date-picker"
                    format="DD-MM-YYYY"
                    value={parseDateValue(newTransactionObj.deedDate)}
                    onChange={(value) => {
                      const formattedDate = serializeDate(value)
                      handleSetStateNewTransactionObj("deedDate", formattedDate)
                    }}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="transaction_price">Total Price</label>
                  <div className="input-with-euro">
                    <span className="euro-sign">€</span>
                    <NumericFormat
                      className="input_field__input_box--text price"
                      id="transaction_price"
                      name="transaction_price"
                      value={newTransactionObj.transactionPrice}
                      thousandSeparator=" "
                      decimalSeparator="," 
                      decimalScale={2}
                      fixedDecimalScale={true}
                      displayType="input"
                      readOnly
                    />
                  </div>
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="notary_buyer">Notary Buyer</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...contacts.map(item => ({ ...item, type: 'contact' })), ...companies.map(item => ({ ...item, type: 'company' }))]}
                    value={selectedNotaryBuyers}
                    onChange={handleSetStateNewTransactionNotaryBuyers}
                    title={"Select notary buyers"}
                    multiselect={true}
                    disabled={isBasisakte}
                  />
                </div>

                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="notary_seller">Notary Seller</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...contacts.map(item => ({ ...item, type: 'contact' })), ...companies.map(item => ({ ...item, type: 'company' }))]}
                    value={selectedNotarySellers}
                    onChange={handleSetStateNewTransactionNotarySellers}
                    title={"Select notary sellers"}
                    multiselect={true}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="broker">Broker</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...contacts.map(item => ({ ...item, type: 'contact' })), ...companies.map(item => ({ ...item, type: 'company' }))]}
                    value={selectedBrokers}
                    onChange={handleSetStateNewTransactionBrokers}
                    title={"Select brokers"}
                    multiselect={true}
                    disabled={isBasisakte}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label">Deal Type</label>
                  <PopupDropdown
                    options={[{ id: 'asset', name: 'Asset deal' }, { id: 'share', name: 'Share deal' }]}
                    value={newTransactionObj.isShareDeal ? { id: 'share', name: 'Share deal' } : { id: 'asset', name: 'Asset deal' }}
                    onChange={(opt) => {
                      const isShare = opt?.id === 'share'
                      setNewTransactionObj(prev => ({
                        ...prev,
                        isShareDeal: isShare,
                        isAssetDeal: !isShare,
                        estimatedAssetValue: isShare ? prev.estimatedAssetValue : ""
                      }))
                      if (!isShare) {
                        setSelectedTransactedCompany(null)
                      }
                    }}
                    title={"Select deal type"}
                    searchBar={false}
                    disabled={isBasisakte}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label">Transacted company</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...companies.map(item => ({ id: item.id, name: item.company_name, type: 'company' }))]}
                    value={selectedTransactedCompany}
                    onChange={(value) => {
                      setSelectedTransactedCompany(value)
                    }}
                    title={"Select company"}
                    multiselect={false}
                    disabled={isBasisakte || !newTransactionObj.isShareDeal}
                  />
                </div>

                {newTransactionObj.isShareDeal && (
                  <>
                    <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                      <label className="input_field__label" htmlFor="estimated_asset_value">Estimated Asset Value</label>
                      <div className="input-with-euro">
                        <span className="euro-sign">€</span>
                        <NumericFormat
                          className="input_field__input_box--text price"
                          id="estimated_asset_value"
                          name="estimated_asset_value"
                          value={newTransactionObj.estimatedAssetValue}
                          thousandSeparator=" "
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          displayType="input"
                          disabled={isBasisakte}
                          onValueChange={(values) => {
                            handleSetStateNewTransactionObj("estimatedAssetValue", values.floatValue ?? "")
                          }}
                        />
                      </div>
                    </div>

                    <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                      <label className="input_field__label" htmlFor="latent_buyer_gain">Latente meerwaarde koper</label>
                      <div className="input-with-euro">
                        <span className="euro-sign">€</span>
                        <NumericFormat
                          className="input_field__input_box--text price"
                          id="latent_buyer_gain"
                          name="latent_buyer_gain"
                          value={(Number(newTransactionObj.estimatedAssetValue) || 0) - (Number(newTransactionObj.transactionPrice) || 0)}
                          thousandSeparator=" "
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale={true}
                          displayType="input"
                          readOnly
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className='panel'>
                <div className='ownership-composers-stack'>
                  <OwnershipComposer
                    title='Sellers'
                    plan={sellerOwnershipPlan}
                    onChange={handleSellerOwnershipPlanChange}
                    contacts={contacts}
                    companies={companies}
                    calculatedShareLabel='Calculated proceeds share'
                  />
                  <OwnershipComposer
                    title='Buyers'
                    plan={buyerOwnershipPlan}
                    onChange={handleBuyerOwnershipPlanChange}
                    contacts={contacts}
                    companies={companies}
                    calculatedShareLabel='Calculated invoice share'
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CreateTransactionPage
